import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useBasket } from '../bff-api/baskets/useBasket';
import { useCustomerGeoLocationStore } from '../bff-api/stores/useCustomerGeoLocationStore';
import { useStore } from '../bff-api/stores/useStore';
import { StorageKey } from './model/StorageKey';
import { useCookieStorageMutation } from './useCookieStorage';
import { useLocalStorage, useLocalStorageMutation } from './useLocalStorage';
import type { Store } from '../bff-api/stores/model';
import type { HookProps } from './model/HookProps';

export const useBopisStore = (props?: HookProps): UseQueryResult<Store | undefined> => {
    const bagStore = useBasketBopisStore();
    const nearestStore = useCustomerGeoLocationStore();
    const storageStore = useLocalStorage<Store | undefined>(StorageKey.BOPIS_STORE);
    const pickedStore = useStore(storageStore?.data?.id);

    if (props?.preferenceForBasketValue && (bagStore.data || bagStore.isFetching)) {
        return bagStore;
    }

    if (pickedStore.data || pickedStore.isFetching) {
        return pickedStore;
    }

    if (nearestStore.data || nearestStore.isFetching) {
        return nearestStore;
    }

    return bagStore;
};

export const useBasketBopisStore = (): UseQueryResult<Store> => {
    const { data } = useBasket();
    return useStore(data?.c_bopisStoreID);
};

export const useBopisStoreMutation = (
    updateStoreIdCookie?: boolean,
): UseMutationResult<unknown, unknown, Store> => {
    const bopisStoreMutation = useLocalStorageMutation(StorageKey.BOPIS_STORE);
    const storeIDMutation = useCookieStorageMutation(StorageKey.STORE_ID);

    return useMutation<unknown, unknown, Store>({
        mutationFn: async (store: Store) => {
            bopisStoreMutation.mutate(store);

            // The cookie is used in the cart by Site Genesis,
            // so we update it only if we are adding a product to the cart.
            if (updateStoreIdCookie) {
                storeIDMutation.mutate(store.id);
            }

            return Promise.resolve();
        },
    });
};
